import { useEffect, useRef } from "react";
import { usePageVisibility, useToastMessage } from "src/contexts";
import {
  ConfiguratorContext_CheckoutConfigFragment,
  useConfiguratorContext_CheckPlanChangeQuery,
} from "./Configurator.context.generated";

type UsePlanChangePollingParams = {
  checkoutConfig: ConfiguratorContext_CheckoutConfigFragment | undefined;
  onPlanChange: () => Promise<unknown>;
};

/**
 * Polls for plan changes to detect when another tab/user has changed the product offering.
 * Shows a toast notification and triggers a refetch when a change is detected.
 */
export function usePlanChangePolling({ checkoutConfig, onPlanChange }: UsePlanChangePollingParams) {
  const isPageVisible = usePageVisibility();
  const { showToast } = useToastMessage();
  // Ref is mostly so we can show correct toast plan name in the toast notificiation once it's changed.
  // if we use a simple const it will show the old plan name in the toast.
  const baselinePlanIdRef = useRef<string | undefined>(checkoutConfig?.productOffering?.id);

  // Initialize/update baseline when config loads
  useEffect(() => {
    if (checkoutConfig?.productOffering?.id) {
      baselinePlanIdRef.current = checkoutConfig.productOffering.id;
    }
  }, [checkoutConfig?.productOffering?.id]);

  const shouldPoll = isPageVisible && !!checkoutConfig?.id;

  const { data } = useConfiguratorContext_CheckPlanChangeQuery({
    variables: { id: checkoutConfig?.id ?? "" },
    // Note: will make a call instantly when the page becomes visible which is a good thing.
    skip: !shouldPoll,
    pollInterval: shouldPoll ? 10000 : undefined,
    fetchPolicy: "network-only", // Always hit the server, never use cache
    errorPolicy: "ignore",
  });

  // Watch for plan changes in the polled data
  useEffect(() => {
    if (!data?.checkoutConfig.productOffering) return;

    const latestPlanId = data.checkoutConfig.productOffering.id;
    const latestPlanName = data.checkoutConfig.productOffering.name;

    // Warn if the plan has been changed by another user or another tab and we're refetching options.
    if (baselinePlanIdRef.current && latestPlanId !== baselinePlanIdRef.current) {
      showToast({
        title: `The plan was changed to "${latestPlanName}". Refreshing options.`,
        variant: "warning",
      });
      baselinePlanIdRef.current = latestPlanId;
      // Trigger the refetch callback
      void onPlanChange();
    }
  }, [data, showToast, onPlanChange]);
}
